/**
 * German translations for Radar component
 * Phase 4.3: Internationalization
 */

export const radarI18nDe = {
  // Component labels
  title: 'Anfragen',
  daysSuffix: 'Tage',
  daysShort: 'd',
  nowLabel: 'JETZT',

  // Zone labels
  zones: {
    panic: 'Panikzone',
    middle: 'Mittelzone',
    outer: 'Außenzone',
  },

  // Status labels
  status: {
    open: 'Offen',
    accepted: 'Angenommen',
    declined: 'Abgelehnt',
    filled: 'Gefüllt',
    booked: 'Gebucht',
    pending: 'Ausstehend',
  },

  // Bubble type labels
  bubbleTypes: {
    outgoing: 'von mir',
    incoming: 'an mich',
  },

  // Button labels
  buttons: {
    refresh: 'Aktualisieren',
    retry: 'Erneut versuchen',
    reload: 'Seite neu laden',
  },

  // Form labels
  form: {
    timeRange: 'Zeitraum',
  },

  // Loading states
  loading: {
    default: 'Radar wird geladen...',
    refreshing: 'Radar wird aktualisiert...',
  },

  // Empty states
  empty: {
    default: 'Keine Vertretungsanfragen im gewählten Zeitraum',
    noResults: 'Keine Anfragen gefunden',
  },

  // Error messages
  errors: {
    title: 'Radar konnte nicht geladen werden',
    description: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    unknown: 'Unbekannter Fehler',
  },

  // Tooltip content
  tooltip: {
    subject: 'Fach',
    date: 'Datum',
    time: 'Zeit',
    room: 'Raum',
    class: 'Klasse',
    status: 'Status',
    teacher: 'Lehrer',
    // frost-v2 (E11/E13): Frost-Chip ersetzt Statuschip + Gate-Zeile
    gateChip: '❄ Wartet auf Direktor',
    gateLine: (date: string) => `Verteilung pausiert · Frist eingefroren seit ${date}`,
    gateLineNoDate: 'Verteilung pausiert · Frist eingefroren',
  },

  // ARIA labels
  aria: {
    regionLabel: 'Vertretungsanfragen Radar',
    canvasLabel: 'Interaktive Visualisierung von Vertretungsanfragen',
    instructions: `Dieser Radar zeigt Vertretungsanfragen nach Dringlichkeit und Zeit an.
Drücken Sie Tab um zwischen Anfragen zu navigieren.
Verwenden Sie die Pfeiltasten um zwischen Anfragen zu wechseln.
Drücken Sie Enter um Details anzuzeigen.
Drücken Sie Escape um die Auswahl aufzuheben.`,
    nowIndicator: 'Jetzt',
    refreshButton: 'Radar aktualisieren',
    timeRangeSlider: 'Zeitraum auswählen',
    // frost-v2 (A2): per-Bubble-Gate-Hinweis für Screenreader
    bubbleGate: 'Wartet auf Direktor-Freigabe',
  },

  // Legend
  legend: {
    outgoingRequests: 'Anfragen von mir',
    incomingRequests: 'Anfragen an mich',
    panicZone: 'Panikzone',
    middleZone: 'Mittelzone',
    hoursShort: 'h',
    splitHint: 'Eine Anfrage kann in bis zu 3 Bubbles gespalten sein: besetzt / abgelehnt / offen.',
    // frost-v2 (E12): Frost-Legende
    gateFrozen: '❄ Wartet auf Direktor',
  },

  // Cascade-Lehrer im Hover (yellow-fix-v2/v3)
  hover: {
    takenOver: 'Übernommen von',
    declinedBy: 'Abgelehnt von',
    stillOpen: 'Noch offen — Kontaktiert',
    more: 'weitere',
  },

  // Prep (Phase 7)
  prep: {
    open: 'Prep offen',
    status: {
      complete: 'Prep vollständig',
      partial: 'Prep teilweise',
      requested: 'Prep angefragt',
      noPrep: 'Kein Prep',
    },
    request: {
      button: 'Prep anfordern',
      title: 'Prep anfordern',
      reason: 'Grund (optional)',
      reasonPlaceholder: 'z.B. Prep unvollständig',
      submit: 'Senden',
      submitting: 'Wird angefragt...',
      success: 'Prep-Anforderung gesendet',
      error: 'Prep-Anforderung fehlgeschlagen',
      alreadyRequested: 'Prep bereits angefragt',
    },
    resolve: {
      button: 'Prep-Anforderung erfüllt',
      loading: 'Wird markiert...',
      success: 'Prep-Anforderung als erfüllt markiert',
      error: 'Aktion fehlgeschlagen',
      reasonLabel: 'Grund',
    },
  },

  // Admin-specific labels
  admin: {
    title: 'Vertretungs-Radar (Admin)',
    teachers: 'Lehrer',
    requests: 'Anfragen',
    panic: 'PANIK',
    requestDetails: 'Anfrage-Details',
    contacted: 'Kontaktiert',
    acceptance: 'Zusage',
    pending: 'Ausstehend',
    declined: 'Abgelehnt',
    substitution: 'Vertretung',
    panicZoneUrgent: 'PANIKZONE — Vertretung dringend!',
    open: 'Offen',
    filled: 'Besetzt',
    requestOpen: 'Anfrage offen',
    summaryOpen: 'Offen',
    summaryFilled: 'Besetzt',
    summaryPanic: 'Panik',
    // bubblenew-v2: Badge-Ring (Kollaps-Griff am Umfang)
    badgeCollapse: 'Bündel kollabieren',
    badgeExpand: 'Bündel aufklappen',
    badgeLessonCount: (n: number) => `${n} ${n === 1 ? 'Lektion' : 'Lektionen'}`,
    hoursUntil: {
      lessThanOne: 'in <1h',
      oneDay: 'in 1 Tag',
      oneDayWithHours: 'in 1 Tag',
      days: 'in Tagen',
    },
    // frost-v2 (E15): Gate-Hinweis-Banner im AdminSlideOver
    gateBanner: '⏸ Wartet auf Direktor-Freigabe — Verteilung pausiert',
  },

  // Slide-over panel (Admin bubble detail)
  slideOver: {
    title: 'Vertretungsanfrage',
    lesson: 'Unterricht',
    participants: 'Beteiligte',
    responses: 'Antworten',
    prep: 'Vorbereitung',
    actions: 'Aktionen',
    date: 'Datum',
    time: 'Zeit',
    subject: 'Fach',
    klass: 'Klasse',
    room: 'Zimmer',
    lessons: 'Lektionen',
    reason: 'Grund',
    notes: 'Notizen',
    created: 'Erstellt',
    requesting: 'Anfragend',
    substitute: 'Vertretung',
    notAssigned: 'Noch nicht zugewiesen',
    panicZone: 'Panikzone',
    middleZone: 'Mittelzone',
    outerZone: 'Außenzone',
    noResponses: 'Noch keine Antworten',
    contacted: 'kontaktiert',
    sendMessage: 'Nachricht senden',
    sendReminder: 'Erinnerung senden',
    assign: 'Vertretung zuweisen',
    cancelRequest: 'Anfrage abbrechen',
    cancelConfirm: 'Anfrage wirklich stornieren? Alle offenen Antworten werden abgelehnt.',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    sending: 'Senden...',
    assigning: 'Zuweisen...',
    cancelling: 'Stornieren...',
    searchTeacher: 'Lehrer suchen...',
    noTeachersFound: 'Keine Lehrer gefunden',
    multiDay: 'Mehrere Tage',
    prepComplete: 'Prep vollständig',
    prepPartial: 'Prep teilweise',
    prepRequested: 'Prep angefragt',
    prepNoInfo: 'Keine Prep-Info',
    requestedFrom: 'Angefragt von',
    moreResponses: 'weitere anzeigen',
    lessResponses: 'weniger anzeigen',
    reminderPlaceholder: 'Optionale Nachricht (sonst Standardtext)...',
    overwriteConfirm: 'Überschreiben',
    alreadyAssigned: 'Bereits zugewiesen an',
    overwriteQuestion: 'Überschreiben?',
    statusLabels: {
      pending: 'Offen',
      accepted: 'Angenommen',
      declined: 'Abgelehnt',
      cancelled: 'Storniert',
      booked: 'Gebucht',
      partially_filled: 'Teilweise',
      filled: 'Gefüllt',
      expired: 'Abgelaufen',
      // frost-v2: Director-Gate-Status statt Rohstring-Fallback
      director_pending: 'Direktor-Freigabe ausstehend',
      director_returned: 'Zurückgestellt (angepasst)',
    },
    lessonStatusLabels: {
      pending: 'Offen',
      assigned: 'Zugewiesen',
      accepted: 'Angenommen',
      declined: 'Abgelehnt',
      cancelled: 'Storniert',
      booked: 'Gebucht',
      expired: 'Abgelaufen',
    },
    cancelErrors: {
      alreadyCancelled: 'Diese Anfrage wurde bereits storniert',
      alreadyBooked: 'Diese Anfrage ist bereits fest gebucht und kann nicht mehr storniert werden',
      alreadyDeclined: 'Diese Anfrage wurde bereits abgelehnt',
      noPermission: 'Keine Berechtigung zum Stornieren dieser Anfrage',
      notFound: 'Anfrage nicht gefunden',
      generic: 'Stornierung fehlgeschlagen',
    },
  },

  // Filter drawer (filter-fix-v2)
  filter: {
    title: 'Filter',
    reset: 'Zurücksetzen',
    close: 'Schliessen',
    activeOnly: 'Nur Aktive (Offen + Interessiert + Übernommen)',
    activeOnlyHint: 'Status-Filter wird durch „Nur Aktive" überschrieben',
    sectionStatus: 'Status',
    sectionZone: 'Dringlichkeit',
    sectionType: 'Typ',
    sectionSubject: 'Fach',
    sectionFaculty: 'Fachbereich',
    sectionClass: 'Klasse',
    sectionRequester: 'Anfragende Lehrperson',
    sectionSpecials: 'Sonderzustände',
    specialPrepOpen: 'Prep offen',
    specialPrepRequested: 'Prep angefordert',
    specialStornoPending: 'Storno ausstehend',
    specialDirectorAction: 'Direktor: Handlung nötig',
    specialHasUnread: 'Ungelesene Nachricht',
    specialIsSplit: 'Split-Bubble',
    specialPartial: 'Teilweise erfüllt',
    specialNeedsAdminAssignment: 'Manuelle Zuweisung nötig',
    // frost-v2 (E12): Filter-Special «Beim Direktor»
    specialDirectorGate: 'Beim Direktor',
    lockedHint: 'Die Filterung ist vom Superadmin gesperrt. Du siehst den schulweiten Standard.',
    badgeAria: (n: number) => `${n} aktive Filter`,
    emptyAll: 'Alle sichtbar',
    emptyFiltered: 'Keine Anfragen gefunden',
    specialsOrHint: 'Mehrere Sonderzustände: Bubble muss mindestens einen erfüllen.',
    statusPending: 'Offen',
    statusInterested: 'Interessiert / Wartepool',
    statusFilled: 'Übernommen',
    statusDeclined: 'Abgelehnt',
    statusCancelled: 'Storniert',
    statusExpired: 'Abgelaufen',
    statusAssignedElsewhere: 'Von anderer übernommen',
    zonePanic: 'Panikzone',
    zoneMiddle: 'Mittelzone',
    zoneOuter: 'Aussenzone',
    typeOutgoing: 'Anfragen von mir',
    typeIncoming: 'Anfragen an mich',
    visibleAll: (n: number) => `${n} Anfragen sichtbar`,
    visiblePartial: (visible: number, total: number) => `${visible} von ${total} Anfragen sichtbar`,
    checklistAll: 'Alle',
    checklistNoneRestriction: 'Keine Einschränkung',
    checklistSearch: 'Suchen…',
    checklistNoHits: 'Keine Treffer',
    checklistAllVisible: 'Alle sichtbar (keine eingeschränkt)',
    countRequests: (visible: number, total: number) => `${visible}/${total} Anfragen`,
  },
}

// Swiss German spelling variant (ß -> ss)
export const radarI18nDeCH = {
  ...radarI18nDe,
  zones: {
    ...radarI18nDe.zones,
    outer: 'Aussenzone',
  },
  slideOver: {
    ...radarI18nDe.slideOver,
    outerZone: 'Aussenzone',
  },
}

export type RadarI18n = typeof radarI18nDe

export default radarI18nDe
